import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useI18n } from '../../context/I18nContext';
import { Project, ProjectStatus, Lead, Payment } from '../../types';
import ProjectCard from './ProjectCard';
import { highlightRing } from '../../styles/colors';
import ScrollFadeContainer from '../ui/ScrollFadeContainer';
import Tooltip from '../ui/Tooltip';
import { Archive, ChevronRight, FileText, Cog, Send, AlertCircle, CheckCircle2, Plus } from 'lucide-react';
import {
  KANBAN_ACTIVE_STATUSES,
  KANBAN_ARCHIVE_STRIP_WIDTH,
  computeKanbanLayout,
  getKanbanVisibleStatuses,
  useMeasuredWidth,
} from './kanbanLayout';

interface KanbanBoardProps {
  projects: Project[];
  archivedCount?: number;
  leads: Lead[];
  payments: Payment[];
  /** When set, the project card with this id gets a highlight ring (e.g. from URL ?project=) */
  highlightedProjectId?: string | null;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
  onUploadFile: (project: Project) => void;
  onMarkComplete: (project: Project) => void;
  onCreatePayment?: (project: Project) => void;
  onViewPayment?: (payment: Payment) => void;
  onMarkPaymentAsPaid?: (payment: Payment) => void;
  onViewLead?: (leadId: string) => void;
  onMoveToNextStatus?: (project: Project) => void;
  onUpdateStatus: (projectId: string, newStatus: ProjectStatus) => Promise<void>;
  onViewArchived?: () => void;
  onStartDiffusion?: (project: Project) => void;
  /** Open create modal with this column status preselected */
  onCreateInColumn?: (status: ProjectStatus) => void;
  /** Search + filters fingerprint — triggers compact focus jump when results move columns */
  filterKey?: string;
  /** Compact mode: apply this status once when returning from archived via chips */
  compactFocusStatus?: ProjectStatus | null;
  onCompactFocusStatusApplied?: () => void;
  /**
   * Width of the board area from a parent that stays mounted across kanban/archived.
   * Avoids chip/layout flash when the board remounts with width 0.
   */
  layoutWidth?: number;
  /**
   * Single-column phone: column grows with the page so `main` scrolls.
   * Parent unlocks page height. Chips scroll with the page (no sticky).
   */
  pageScroll?: boolean;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  projects,
  archivedCount = 0,
  leads,
  payments,
  highlightedProjectId = null,
  onEdit,
  onDelete,
  onUploadFile,
  onMarkComplete,
  onCreatePayment,
  onViewPayment,
  onMarkPaymentAsPaid,
  onViewLead,
  onMoveToNextStatus,
  onUpdateStatus,
  onViewArchived,
  onStartDiffusion,
  onCreateInColumn,
  filterKey = '',
  compactFocusStatus = null,
  onCompactFocusStatusApplied,
  layoutWidth,
  pageScroll = false,
}) => {
  const { t } = useI18n();
  const { ref: measureRef, width: measuredWidth } = useMeasuredWidth();
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const setBoardRootRef = (node: HTMLDivElement | null) => {
    scrollRootRef.current = node;
    measureRef(node);
  };
  // Prefer parent width (stable across archived ↔ kanban); fall back to self-measure.
  const boardWidth = layoutWidth != null && layoutWidth > 0 ? layoutWidth : measuredWidth;
  const hasArchiveStrip = Boolean(onViewArchived);
  const { isFullBoard, visibleColumnCount } = useMemo(
    () => computeKanbanLayout(boardWidth, { hasArchiveStrip }),
    [boardWidth, hasArchiveStrip]
  );
  const isSingleColumn = !isFullBoard && visibleColumnCount <= 1;
  /** Grow with page + sticky chips (phone). Prefer parent flag so layout stays in sync. */
  const usePageScroll = pageScroll || isSingleColumn;
  const columnAreaRef = useRef<HTMLDivElement | null>(null);
  const [pageScrollColumnMinH, setPageScrollColumnMinH] = useState<number | undefined>(undefined);

  const [draggedProject, setDraggedProject] = useState<Project | null>(null);
  const [draggedHeight, setDraggedHeight] = useState<number>(120);
  const [dragOverColumn, setDragOverColumn] = useState<ProjectStatus | null>(null);
  /** Chip-bar hover only — not set when dragging over a visible column body */
  const [dragOverChip, setDragOverChip] = useState<ProjectStatus | null>(null);
  /** Compact focus status (chips). Full board ignores this for layout. */
  const [mobileStatus, setMobileStatus] = useState<ProjectStatus>(() =>
    compactFocusStatus && KANBAN_ACTIVE_STATUSES.includes(compactFocusStatus)
      ? compactFocusStatus
      : 'BRIEF_RECEIVED'
  );
  /** User picked an empty chip on purpose (e.g. to create) — don't steal focus until data/filters change. */
  const stickToEmptyFocusRef = useRef(
    !!(compactFocusStatus && KANBAN_ACTIVE_STATUSES.includes(compactFocusStatus))
  );
  const filterKeyRef = useRef(filterKey);

  useEffect(() => {
    if (!compactFocusStatus) return;
    setMobileStatus(compactFocusStatus);
    // Intentional chip nav from archived — never auto-jump to first non-empty column.
    stickToEmptyFocusRef.current = true;
    onCompactFocusStatusApplied?.();
    // Intentionally only when compactFocusStatus changes (callback cleared by parent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compactFocusStatus]);

  // Single-column page scroll: reset main scroll when changing status chip.
  useEffect(() => {
    if (!usePageScroll) return;
    const root = scrollRootRef.current;
    const scrollParent = root?.closest('main') ?? root;
    scrollParent?.scrollTo({ top: 0 });
  }, [mobileStatus, usePageScroll]);

  // Drag-and-drop : deux contextes distincts.
  // (1) Carte projet : handleDragStart définit draggedProject → colonnes affichent "Déposer ici" et acceptent le drop.
  // (2) Élément extérieur (fichiers, lien, etc.) : draggedProject reste null → pas de zone de drop sur les colonnes ; le dépôt de fichiers est géré par ProjectCard.

  const columns: Array<{ status: ProjectStatus; label: string }> = [
    { status: 'BRIEF_RECEIVED', label: t('projects.statusBriefReceived') },
    { status: 'IN_PRODUCTION', label: t('projects.statusInProduction') },
    { status: 'V1_SENT', label: t('projects.statusFirstVersion') },
    { status: 'CHANGES_REQUESTED', label: t('projects.statusCorrections') },
    { status: 'DELIVERED', label: t('projects.statusDelivered') },
  ];

  const projectsByStatus = useMemo(() => {
    const grouped: Record<ProjectStatus, Project[]> = {
      BRIEF_RECEIVED: [],
      IN_PRODUCTION: [],
      V1_SENT: [],
      CHANGES_REQUESTED: [],
      DELIVERED: [],
      ARCHIVED: [],
    };

    projects.forEach((project) => {
      if (grouped[project.status]) {
        grouped[project.status].push(project);
      }
    });

    Object.keys(grouped).forEach((status) => {
      grouped[status as ProjectStatus].sort((a, b) => {
        if (a.deadline && b.deadline) {
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        }
        if (a.deadline && !b.deadline) return -1;
        if (!a.deadline && b.deadline) return 1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
    });

    return grouped;
  }, [projects]);

  const visibleStatuses = useMemo(
    () => getKanbanVisibleStatuses(mobileStatus, visibleColumnCount),
    [mobileStatus, visibleColumnCount]
  );

  // Jump when focus has no results elsewhere — stay if another column in the visible
  // window already shows a match (no need to reshuffle when count > 1).
  useEffect(() => {
    if (isFullBoard) return;

    const filterChanged = filterKey !== filterKeyRef.current;
    filterKeyRef.current = filterKey;

    const focusCount = projectsByStatus[mobileStatus]?.length ?? 0;
    if (focusCount > 0) {
      stickToEmptyFocusRef.current = false;
      return;
    }

    const windowStatuses = getKanbanVisibleStatuses(mobileStatus, visibleColumnCount);
    const windowHasResults = windowStatuses.some((s) => (projectsByStatus[s]?.length ?? 0) > 0);
    if (visibleColumnCount > 1 && windowHasResults) {
      stickToEmptyFocusRef.current = false;
      return;
    }

    const withWork = KANBAN_ACTIVE_STATUSES.find((s) => (projectsByStatus[s]?.length ?? 0) > 0);
    if (!withWork || withWork === mobileStatus) return;

    if (stickToEmptyFocusRef.current && !filterChanged) return;

    stickToEmptyFocusRef.current = false;
    setMobileStatus(withWork);
  }, [filterKey, projectsByStatus, mobileStatus, visibleColumnCount, isFullBoard]);

  const handleDragStart = (e: React.DragEvent, project: Project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
    const target = e.currentTarget as HTMLElement;
    if (target) {
      setDraggedHeight(target.offsetHeight);
      target.style.opacity = '0.5';
      const card = target.querySelector('[data-project-card]');
      if (card instanceof HTMLElement) {
        card.style.cursor = 'grabbing';
      }
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedProject(null);
    setDragOverColumn(null);
    setDragOverChip(null);
    const target = e.currentTarget as HTMLElement;
    if (target) {
      target.style.opacity = '1';
      const card = target.querySelector('[data-project-card]');
      if (card instanceof HTMLElement) {
        card.style.cursor = 'grab';
      }
    }
  };

  const handleDragOver = (e: React.DragEvent, status: ProjectStatus) => {
    if (!draggedProject) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(status);
    setDragOverChip(null);
  };

  const handleChipDragOver = (e: React.DragEvent, status: ProjectStatus) => {
    if (!draggedProject) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverChip(status);
    setDragOverColumn(null);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!target.contains(relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleChipDragLeave = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!target.contains(relatedTarget)) {
      setDragOverChip(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: ProjectStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDragOverChip(null);
    if (!draggedProject) return;

    if (draggedProject.status === targetStatus) {
      setDraggedProject(null);
      return;
    }

    try {
      await onUpdateStatus(draggedProject.id, targetStatus);
    } catch (error) {
      console.error('Error updating project status:', error);
    } finally {
      setDraggedProject(null);
    }
  };

  const getColumnColor = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'border-blue-200/60 bg-blue-50/20',
      IN_PRODUCTION: 'border-violet-200/60 bg-violet-50/20',
      V1_SENT: 'border-amber-200/60 bg-amber-50/20',
      CHANGES_REQUESTED: 'border-orange-200/60 bg-orange-50/20',
      DELIVERED: 'border-emerald-200/60 bg-emerald-50/20',
      ARCHIVED: 'border-slate-200/60 bg-slate-50/20',
    };
    return colors[status] || 'border-gray-200/60 bg-gray-50/20';
  };

  const getHeaderGradient = (status: ProjectStatus) => {
    const gradients: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'from-blue-500 to-cyan-500',
      IN_PRODUCTION: 'from-violet-500 to-purple-500',
      V1_SENT: 'from-amber-500 to-yellow-500',
      CHANGES_REQUESTED: 'from-orange-500 to-rose-500',
      DELIVERED: 'from-emerald-500 to-green-500',
      ARCHIVED: 'from-slate-400 to-gray-400',
    };
    return gradients[status] || 'from-gray-400 to-gray-400';
  };

  const getStatusIcon = (status: ProjectStatus) => {
    const icons: Record<ProjectStatus, React.ReactNode> = {
      BRIEF_RECEIVED: <FileText className="w-4 h-4" />,
      IN_PRODUCTION: <Cog className="w-4 h-4" />,
      V1_SENT: <Send className="w-4 h-4" />,
      CHANGES_REQUESTED: <AlertCircle className="w-4 h-4" />,
      DELIVERED: <CheckCircle2 className="w-4 h-4" />,
      ARCHIVED: <Archive className="w-4 h-4" />,
    };
    return icons[status] || <FileText className="w-4 h-4" />;
  };

  const getStatusTextColor = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'text-blue-700',
      IN_PRODUCTION: 'text-violet-700',
      V1_SENT: 'text-amber-700',
      CHANGES_REQUESTED: 'text-orange-700',
      DELIVERED: 'text-emerald-700',
      ARCHIVED: 'text-slate-700',
    };
    return colors[status] || 'text-gray-900';
  };

  const getBadgeColor = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'bg-blue-100/80 text-blue-700',
      IN_PRODUCTION: 'bg-violet-100/80 text-violet-700',
      V1_SENT: 'bg-amber-100/80 text-amber-700',
      CHANGES_REQUESTED: 'bg-orange-100/80 text-orange-700',
      DELIVERED: 'bg-emerald-100/80 text-emerald-700',
      ARCHIVED: 'bg-slate-100/80 text-slate-700',
    };
    return colors[status] || 'bg-gray-100/80 text-gray-700';
  };

  const getChipActiveClass = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'bg-blue-600 text-white border-blue-600',
      IN_PRODUCTION: 'bg-violet-600 text-white border-violet-600',
      V1_SENT: 'bg-amber-600 text-white border-amber-600',
      CHANGES_REQUESTED: 'bg-orange-600 text-white border-orange-600',
      DELIVERED: 'bg-emerald-600 text-white border-emerald-600',
      ARCHIVED: 'bg-slate-600 text-white border-slate-600',
    };
    return colors[status] || 'bg-gray-800 text-white border-gray-800';
  };

  const getChipSoftClass = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'border-blue-300 bg-blue-50 text-blue-800',
      IN_PRODUCTION: 'border-violet-300 bg-violet-50 text-violet-800',
      V1_SENT: 'border-amber-300 bg-amber-50 text-amber-800',
      CHANGES_REQUESTED: 'border-orange-300 bg-orange-50 text-orange-800',
      DELIVERED: 'border-emerald-300 bg-emerald-50 text-emerald-800',
      ARCHIVED: 'border-slate-300 bg-slate-50 text-slate-800',
    };
    return colors[status] || 'border-gray-300 bg-gray-50 text-gray-800';
  };

  const getChipSurfaceClass = (
    status: ProjectStatus,
    options: { active: boolean; inWindow: boolean }
  ) => {
    if (options.active) return getChipActiveClass(status);
    if (options.inWindow) return getChipSoftClass(status);
    return 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50';
  };

  const getDragOverBorderColor = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'border-blue-500',
      IN_PRODUCTION: 'border-violet-500',
      V1_SENT: 'border-amber-500',
      CHANGES_REQUESTED: 'border-orange-500',
      DELIVERED: 'border-emerald-500',
      ARCHIVED: 'border-slate-400',
    };
    return colors[status] || 'border-blue-500';
  };

  const getDragOverShadowColor = (status: ProjectStatus) => {
    const colors: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'shadow-blue-500/30',
      IN_PRODUCTION: 'shadow-violet-500/30',
      V1_SENT: 'shadow-amber-500/30',
      CHANGES_REQUESTED: 'shadow-orange-500/30',
      DELIVERED: 'shadow-emerald-500/30',
      ARCHIVED: 'shadow-slate-400/30',
    };
    return colors[status] || 'shadow-blue-500/30';
  };

  const getDropHereColors = (status: ProjectStatus) => {
    const configs: Record<ProjectStatus, { border: string; bg: string; text: string }> = {
      BRIEF_RECEIVED: { border: 'border-blue-400', bg: 'bg-blue-50/60', text: 'text-blue-600' },
      IN_PRODUCTION: { border: 'border-violet-400', bg: 'bg-violet-50/60', text: 'text-violet-600' },
      V1_SENT: { border: 'border-amber-400', bg: 'bg-amber-50/60', text: 'text-amber-600' },
      CHANGES_REQUESTED: { border: 'border-orange-400', bg: 'bg-orange-50/60', text: 'text-orange-600' },
      DELIVERED: { border: 'border-emerald-400', bg: 'bg-emerald-50/60', text: 'text-emerald-600' },
      ARCHIVED: { border: 'border-slate-400', bg: 'bg-slate-50/60', text: 'text-slate-600' },
    };
    return configs[status] || { border: 'border-primary-400', bg: 'bg-primary-50/60', text: 'text-primary-600' };
  };

  const isArchivedDragOver = dragOverColumn === 'ARCHIVED';
  const showChipLabels = visibleColumnCount >= 2;

  // Phone page-scroll: column at least fills remaining viewport (like locked board), can grow with content.
  useLayoutEffect(() => {
    if (!usePageScroll) {
      setPageScrollColumnMinH(undefined);
      return;
    }
    const el = columnAreaRef.current;
    if (!el) return;

    const update = () => {
      const main = el.closest('main');
      const mainScroll = main instanceof HTMLElement ? main.scrollTop : 0;
      const rect = el.getBoundingClientRect();
      // Top if main were scrolled to 0 (stable while user scrolls the page).
      const topAtRest = rect.top + mainScroll;
      const bottomPad = 12;
      setPageScrollColumnMinH(Math.max(200, Math.round(window.innerHeight - topAtRest - bottomPad)));
    };

    update();
    window.addEventListener('resize', update);
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => update()) : null;
    ro?.observe(el);
    const main = el.closest('main');
    if (main) ro?.observe(main);

    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [usePageScroll, mobileStatus, showChipLabels, visibleColumnCount]);

  const shortStatusLabel = (status: ProjectStatus) => {
    const keys: Record<ProjectStatus, string> = {
      BRIEF_RECEIVED: 'projects.statusBriefShort',
      IN_PRODUCTION: 'projects.statusProductionShort',
      V1_SENT: 'projects.statusV1Short',
      CHANGES_REQUESTED: 'projects.statusCorrectionsShort',
      DELIVERED: 'projects.statusDeliveredShort',
      ARCHIVED: 'projects.statusArchivedShort',
    };
    return t(keys[status]);
  };

  const renderProjectCards = (columnProjects: Project[], options: { enableDrag: boolean }) => {
    const { enableDrag } = options;
    return columnProjects.map((project) => {
      const lead = leads.find((l) => l.id === project.leadId);
      const payment = payments.find((i) => i.projectId === project.id);
      const isDragging = draggedProject?.id === project.id;
      const isOtherDragging = Boolean(draggedProject && !isDragging);

      const card = (
        <div
          data-project-id={project.id}
          className={`rounded-xl transition-all duration-300 ${project.id === highlightedProjectId ? highlightRing.combinedWithShadow : ''}`}
        >
          <ProjectCard
            project={project}
            lead={lead}
            payment={payment}
            onEdit={onEdit}
            onDelete={onDelete}
            onUploadFile={onUploadFile}
            onMarkComplete={onMarkComplete}
            onCreatePayment={onCreatePayment}
            onViewPayment={onViewPayment}
            onMarkPaymentAsPaid={onMarkPaymentAsPaid}
            onViewLead={onViewLead}
            onMoveToNextStatus={onMoveToNextStatus}
            onStartDiffusion={onStartDiffusion}
          />
        </div>
      );

      if (!enableDrag) {
        return <div key={project.id}>{card}</div>;
      }

      return (
        <div
          key={project.id}
          draggable={!isOtherDragging}
          onDragStart={(e) => handleDragStart(e, project)}
          onDragEnd={handleDragEnd}
          className={`cursor-grab active:cursor-grabbing transition-all duration-150 ${
            isDragging ? 'scale-[1.02] opacity-90 shadow-2xl rotate-1' : ''
          } ${isOtherDragging ? 'pointer-events-none' : ''}`}
        >
          {card}
        </div>
      );
    });
  };

  const renderCompactColumn = (
    status: ProjectStatus,
    options: { scrollBody: boolean } = { scrollBody: true }
  ) => {
    const { scrollBody } = options;
    const column = columns.find((c) => c.status === status) || columns[0];
    const columnProjects = projectsByStatus[status] || [];
    const isDragOver = dragOverColumn === status;
    const fillAvailable = !scrollBody;

    const body = (
      <div
        className={`space-y-2 px-2 pb-1 pt-2 ${fillAvailable ? 'flex min-h-0 flex-1 flex-col' : ''}`}
      >
        {columnProjects.length === 0 && !isDragOver ? (
          <div
            className={`flex items-center justify-center text-sm text-gray-400 ${
              fillAvailable ? 'min-h-[8rem] flex-1' : 'h-32'
            }`}
          >
            {t('projects.noProjects')}
          </div>
        ) : (
          renderProjectCards(columnProjects, { enableDrag: true })
        )}

        {isDragOver &&
          draggedProject?.status !== status &&
          (() => {
            const colors = getDropHereColors(status);
            return (
              <div
                className={`flex items-center justify-center rounded-xl border-2 border-dashed text-center shadow-sm ${colors.border} ${colors.bg}`}
                style={{ height: draggedHeight }}
              >
                <div className={`text-sm font-medium ${colors.text}`}>
                  {t('projects.dropHere') || 'Déposer ici'}
                </div>
              </div>
            );
          })()}
      </div>
    );

    return (
      <div
        className={`flex flex-col overflow-hidden rounded-xl border-2 shadow-sm bg-white/50 backdrop-blur-sm outline-none focus:outline-none ${getColumnColor(status)} ${
          scrollBody ? 'min-h-0 flex-1' : fillAvailable ? 'min-h-full flex-1' : ''
        } ${
          isDragOver
            ? `${getDragOverBorderColor(status)} border-dashed shadow-lg ${getDragOverShadowColor(status)}`
            : 'border-gray-200/60'
        } transition-all`}
        onDragOver={(e) => {
          e.preventDefault();
          handleDragOver(e, status);
        }}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, status)}
      >
        <div className="flex-shrink-0 rounded-t-xl bg-white shadow-sm">
          <div className={`h-1 rounded-t-xl bg-gradient-to-r ${getHeaderGradient(status)}`} />
          <div className="flex items-center justify-between gap-2 border-b border-gray-200/60 px-3 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <div className={`flex-shrink-0 ${getStatusTextColor(status)}`}>
                {getStatusIcon(status)}
              </div>
              <h3 className={`truncate text-sm font-semibold ${getStatusTextColor(status)}`}>
                {column.label}
              </h3>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1">
              <span
                className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-semibold ${getBadgeColor(status)}`}
              >
                {columnProjects.length}
              </span>
              {onCreateInColumn ? (
                <Tooltip content={t('projects.newProject')}>
                  <button
                    type="button"
                    onClick={() => onCreateInColumn(status)}
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${getStatusTextColor(status)} hover:bg-black/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
                    aria-label={t('projects.newProject')}
                  >
                    <Plus className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                  </button>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>

        {scrollBody ? <ScrollFadeContainer className="flex-1">{body}</ScrollFadeContainer> : body}
      </div>
    );
  };

  return (
    <div
      ref={setBoardRootRef}
      className={`relative min-h-0 ${usePageScroll ? '' : 'h-full'}`}
    >
      {!isFullBoard ? (
        <div
          className={`flex flex-col ${usePageScroll ? '' : 'h-full min-h-0 gap-2'}`}
        >
          <div className={`flex-shrink-0 ${usePageScroll ? 'pb-2' : ''}`}>
            <div
              className="flex w-full items-center gap-1"
              role="tablist"
              aria-label={t('projects.title')}
            >
              {columns.map((column) => {
                const count = projectsByStatus[column.status]?.length ?? 0;
                const active = mobileStatus === column.status;
                const inWindow = !active && visibleStatuses.includes(column.status);
                const chipDragOver = Boolean(draggedProject && dragOverChip === column.status);
                return (
                  <Tooltip
                    key={column.status}
                    content={column.label}
                    delay={showChipLabels ? 700 : 350}
                    wrapperClassName="flex min-w-0 flex-1"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={active}
                      aria-label={`${column.label} (${count})`}
                      onClick={() => {
                        if (draggedProject) return;
                        const empty = (projectsByStatus[column.status]?.length ?? 0) === 0;
                        stickToEmptyFocusRef.current = empty;
                        setMobileStatus(column.status);
                      }}
                      onDragOver={(e) => handleChipDragOver(e, column.status)}
                      onDragLeave={handleChipDragLeave}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleDrop(e, column.status);
                      }}
                      className={`relative inline-flex w-full flex-row items-center justify-center gap-1 rounded-xl border px-1 py-1.5 transition-colors ${
                        chipDragOver
                          ? `${getDragOverBorderColor(column.status)} border-dashed ${getChipActiveClass(column.status)} opacity-90`
                          : getChipSurfaceClass(column.status, { active, inWindow })
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 ${active ? 'text-white/90' : getStatusTextColor(column.status)}`}
                      >
                        {getStatusIcon(column.status)}
                      </span>
                      {showChipLabels ? (
                        <span
                          className={`min-w-0 truncate text-[10px] font-semibold leading-none ${
                            active ? 'text-white/90' : getStatusTextColor(column.status)
                          }`}
                        >
                          {shortStatusLabel(column.status)}
                        </span>
                      ) : null}
                      <span
                        className={`flex-shrink-0 text-[10px] font-semibold tabular-nums leading-none ${
                          active ? 'text-white/90' : getStatusTextColor(column.status)
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  </Tooltip>
                );
              })}
              {onViewArchived ? (
                <Tooltip
                  content={t('projects.statusArchived')}
                  delay={showChipLabels ? 700 : 350}
                  wrapperClassName="flex min-w-0 flex-1"
                >
                  <button
                    type="button"
                    aria-label={`${t('projects.statusArchived')} (${archivedCount || 0})`}
                    onClick={() => {
                      if (draggedProject) return;
                      onViewArchived();
                    }}
                    onDragOver={(e) => handleChipDragOver(e, 'ARCHIVED')}
                    onDragLeave={handleChipDragLeave}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void handleDrop(e, 'ARCHIVED');
                    }}
                    className={`relative inline-flex w-full flex-row items-center justify-center gap-1 rounded-xl border px-1 py-1.5 transition-colors ${
                      draggedProject && dragOverChip === 'ARCHIVED'
                        ? `${getDragOverBorderColor('ARCHIVED')} border-dashed ${getChipActiveClass('ARCHIVED')} opacity-90`
                        : getChipSurfaceClass('ARCHIVED', { active: false, inWindow: false })
                    }`}
                  >
                    <Archive
                      className={`h-4 w-4 flex-shrink-0 ${
                        draggedProject && dragOverChip === 'ARCHIVED' ? 'text-white/90' : 'text-slate-500'
                      }`}
                    />
                    {showChipLabels ? (
                      <span
                        className={`min-w-0 truncate text-[10px] font-semibold leading-none ${
                          draggedProject && dragOverChip === 'ARCHIVED' ? 'text-white/90' : 'text-slate-500'
                        }`}
                      >
                        {t('projects.statusArchivedShort')}
                      </span>
                    ) : null}
                    <span
                      className={`flex-shrink-0 text-[10px] font-semibold tabular-nums leading-none ${
                        draggedProject && dragOverChip === 'ARCHIVED' ? 'text-white/90' : 'text-slate-500'
                      }`}
                    >
                      {archivedCount || 0}
                    </span>
                  </button>
                </Tooltip>
              ) : null}
            </div>
          </div>

          <div
            ref={columnAreaRef}
            className={
              usePageScroll ? 'flex flex-col' : 'flex min-h-0 flex-1 gap-2'
            }
            style={
              usePageScroll && pageScrollColumnMinH != null
                ? { minHeight: pageScrollColumnMinH }
                : undefined
            }
          >
            {visibleStatuses.map((status) => (
              <div
                key={status}
                className={
                  usePageScroll
                    ? 'flex min-h-full w-full flex-1 flex-col'
                    : 'flex min-h-0 min-w-0 flex-1 flex-col'
                }
              >
                {renderCompactColumn(status, { scrollBody: !usePageScroll })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="
            kanban-x-scroll absolute inset-x-0 top-0 overflow-x-hidden overflow-y-hidden
            h-[calc(100%+0.5rem)]
            sm:h-[calc(100%+0.75rem)]
            2xl:h-[calc(100%+1rem)]
            px-0 lg:-mx-6 lg:px-6
          "
        >
          <div
            className="
              flex min-h-0 min-w-max space-x-4
              h-[calc(100%-0.5rem)]
              sm:h-[calc(100%-0.75rem)]
              2xl:h-[calc(100%-1rem)]
            "
          >
            {columns.map((column) => {
              const columnProjects = projectsByStatus[column.status] || [];
              const isDragOver = dragOverColumn === column.status;

              return (
                <div
                  key={column.status}
                  className={`h-full flex-shrink-0 w-[280px] sm:w-[320px] md:flex-1 md:min-w-[280px] lg:min-w-[300px] flex flex-col overflow-hidden ${getColumnColor(column.status)} rounded-xl border-2 shadow-sm bg-white/50 backdrop-blur-sm outline-none focus:outline-none ${
                    isDragOver
                      ? `${getDragOverBorderColor(column.status)} border-dashed shadow-lg ${getDragOverShadowColor(column.status)}`
                      : 'border-gray-200/60'
                  } transition-all`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    handleDragOver(e, column.status);
                  }}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column.status)}
                  tabIndex={-1}
                >
                  <div className="flex-shrink-0 rounded-t-xl bg-white shadow-sm">
                    <div className={`h-1 rounded-t-xl bg-gradient-to-r ${getHeaderGradient(column.status)}`} />
                    <div className="border-b border-gray-200/60 px-3 py-3 sm:px-4 sm:py-3.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center space-x-2 sm:space-x-2.5">
                          <div className={`flex-shrink-0 ${getStatusTextColor(column.status)}`}>
                            {getStatusIcon(column.status)}
                          </div>
                          <h3
                            className={`truncate text-xs font-semibold sm:text-sm ${getStatusTextColor(column.status)}`}
                          >
                            {column.label}
                          </h3>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <span
                            className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-semibold ${getBadgeColor(column.status)}`}
                          >
                            {columnProjects.length}
                          </span>
                          {onCreateInColumn && (
                            <Tooltip content={t('projects.newProject')}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCreateInColumn(column.status);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${getStatusTextColor(column.status)} hover:bg-black/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
                                aria-label={t('projects.newProject')}
                              >
                                <Plus className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <ScrollFadeContainer className="flex-1">
                    <div className="space-y-2 px-2 pb-1 pt-2 sm:space-y-3 sm:px-3.5 sm:pt-3.5">
                      {columnProjects.length === 0 && !isDragOver && (
                        <div className="flex h-32 items-center justify-center text-sm text-gray-400">
                          {t('projects.noProjects')}
                        </div>
                      )}

                      {renderProjectCards(columnProjects, { enableDrag: true })}

                      {isDragOver &&
                        draggedProject?.status !== column.status &&
                        (() => {
                          const colors = getDropHereColors(column.status);
                          return (
                            <div
                              className={`flex items-center justify-center rounded-xl border-2 border-dashed text-center shadow-sm ${colors.border} ${colors.bg}`}
                              style={{ height: draggedHeight }}
                            >
                              <div className={`text-sm font-medium ${colors.text}`}>
                                {t('projects.dropHere') || 'Déposer ici'}
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  </ScrollFadeContainer>
                </div>
              );
            })}

            {onViewArchived && (
              <div
                className={`relative flex h-full w-20 flex-shrink-0 cursor-pointer flex-col overflow-hidden rounded-xl border-2 bg-white/50 shadow-sm outline-none backdrop-blur-sm focus:outline-none ${
                  isArchivedDragOver
                    ? `${getDragOverBorderColor('ARCHIVED')} border-dashed shadow-lg ${getDragOverShadowColor('ARCHIVED')}`
                    : 'border-gray-200/60'
                } group transition-all hover:border-gray-300 hover:bg-white/70 hover:shadow-md`}
                style={{ flex: '0 0 auto', width: `${KANBAN_ARCHIVE_STRIP_WIDTH}px` }}
                onClick={onViewArchived}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDragOver(e, 'ARCHIVED');
                }}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDrop(e, 'ARCHIVED');
                }}
                tabIndex={-1}
              >
                <div
                  className="absolute inset-0 rounded-lg opacity-[0.08]"
                  style={{
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0, 0, 0, 0.1) 10px, rgba(0, 0, 0, 0.1) 20px)`,
                  }}
                />

                <div className="flex-shrink-0 rounded-t-xl bg-white shadow-sm">
                  <div className={`h-1 rounded-t-xl bg-gradient-to-r ${getHeaderGradient('ARCHIVED')}`} />
                  <div className="border-b border-gray-200/60 px-2 py-2.5 sm:px-3 sm:py-3.5">
                    <div className="flex items-center justify-center">
                      <span
                        className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-xs font-semibold sm:h-6 sm:min-w-[22px] sm:px-1.5 ${getBadgeColor('ARCHIVED')} group-hover:opacity-80 transition-colors`}
                      >
                        {archivedCount || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="relative min-h-0 flex-1">
                  <div
                    className={`flex h-full items-center justify-center overflow-x-hidden overflow-y-auto ${
                      isArchivedDragOver ? 'bg-primary-50/30' : ''
                    }`}
                  >
                    {!isArchivedDragOver && (
                      <div className="flex items-center justify-center space-x-1 px-2 py-2 sm:space-x-1.5 sm:px-3 sm:py-3">
                        <Archive className="h-4 w-4 text-gray-400 transition-colors group-hover:text-gray-600 sm:h-5 sm:w-5" />
                        <ChevronRight className="hidden h-3 w-3 text-gray-400 transition-colors group-hover:text-gray-600 sm:block sm:h-3.5 sm:w-3.5" />
                      </div>
                    )}
                  </div>

                  {isArchivedDragOver &&
                    (() => {
                      const colors = getDropHereColors('ARCHIVED');
                      return (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className={`mb-1 text-sm font-medium ${colors.text}`}>
                              {t('projects.dropHere') || 'Déposer ici'}
                            </div>
                            <div className="text-xs text-gray-500">{t('projects.statusArchived')}</div>
                          </div>
                        </div>
                      );
                    })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;
